import React, { useState, useMemo, useEffect, FC, MouseEvent, memo } from 'react'
import classnames from 'classnames'
import { SelectOption, SelectBase, ISelectOption } from 'components/forms/Selects'
import { IconButton } from 'transfix-ui/components/IconButton'
import { createStyles, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { DownArrowIcon, CloseIcon } from 'transfix-icons'
import { Spinner } from '../Spinner'

export interface IFilterMultiSelectOption extends ISelectOption<string> {
  selected: boolean
  tooltip?: JSX.Element
}

export interface IFilterMultiSelectProps {
  id: string
  dataTest: string
  icon?: JSX.Element
  label: string
  options: IFilterMultiSelectOption[]
  onSelect: (selected: IFilterMultiSelectOption[]) => void
  loading?: boolean
  className?: string
  optionsLabel?: string
}

const useFilterMultiSelectStyles = makeStyles(theme =>
  createStyles<string, IFilterMultiSelectProps>({
    text: {
      color: brandColors.black,
      whiteSpace: 'nowrap',
      paddingRight: theme.spacing(1),
      marginLeft: ({ icon }) => (icon ? theme.spacing(2) : undefined),
      paddingLeft: ({ icon }) => (icon ? theme.spacing(1) : undefined),
    },
    tag: {
      lineHeight: 0.9,
      backgroundColor: brandColors.skyBlue2,
      color: brandColors.skyBlue7,
      marginRight: theme.spacing(0.5),
      borderRadious: 2,
      fontSize: '0.75rem',
      minHeight: 20,
      minWidth: 20,
      padding: 5,
    },
    autocomplete: {
      display: 'inline-block',
    },
    textContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
    },
    prefixIcon: {
      paddingRight: theme.spacing(1),
      minHeight: 20,
      position: 'absolute',
      color: brandColors.black,
    },
  })
)

export const FilterMultiSelect: FC<IFilterMultiSelectProps> = memo(props => {
  const { className, dataTest, icon, id, label, loading, onSelect, options, optionsLabel } = props
  const styles = useFilterMultiSelectStyles(props)
  const [selectedOptions, setSelectedOptions] = useState<IFilterMultiSelectOption[]>(options)
  const [expandedState, setExpandedState] = useState<boolean>(false)
  const clearedOptions = options.map(o => ({ ...o, selected: false }))

  useEffect(() => {
    // Discard changes if closed before clicking apply
    if (!expandedState) {
      setSelectedOptions(options)
    }
  }, [expandedState, setSelectedOptions, options])

  const clearAllOptionsHandler = () => {
    setSelectedOptions(clearedOptions)
  }

  const applyFiltersHandler = () => {
    onSelect(selectedOptions)
  }

  const selectedCount = useMemo(() => {
    return options.reduce((count, option) => {
      if (option.selected) count += 1
      return count
    }, 0)
  }, [options])

  const clearFromPillHandler = (e: MouseEvent) => {
    onSelect(clearedOptions)
    setSelectedOptions(clearedOptions)
    if (!expandedState) {
      e.stopPropagation()
    }
  }

  const interactionIcon = selectedCount ? (
    <>
      <span className={styles.tag} data-test='multi-select-values-count'>
        {selectedCount}
      </span>
      <div>
        <IconButton
          onClick={clearFromPillHandler}
          Icon={CloseIcon}
          label='clear filter'
          data-test='clear-button'
          color='tertiary'
          size='small'
          noPadding
        />
      </div>
    </>
  ) : (
    <DownArrowIcon size='medium' />
  )

  const selectOptionHandler = (selection: ISelectOption) => {
    const selectedIndex = selectedOptions.findIndex(option => option.value === selection.value)
    if (selectedIndex !== -1) {
      setSelectedOptions(
        selectedOptions.map((option, idx) => {
          if (idx === selectedIndex) {
            return { ...option, selected: !option.selected }
          }
          return { ...option }
        })
      )
    }
  }

  const multiSelectClass = classnames(styles.autocomplete, className)

  return (
    <SelectBase
      applied={Boolean(selectedCount)}
      id={id}
      dataTest={dataTest}
      className={multiSelectClass}
      selectButtonType='filter-pill'
      onClear={clearAllOptionsHandler}
      applyFilter={applyFiltersHandler}
      stateCallback={setExpandedState}
      displayValue={
        <div className={styles.textContainer}>
          <div className={styles.prefixIcon}>{icon}</div>
          <b className={styles.text}>{label}</b>
          {loading ? <Spinner position='relative' /> : interactionIcon}
        </div>
      }
      optionsLabel={optionsLabel}>
      {selectedOptions.map(
        ({ label: optionLabel, selected: selectedValue, tooltip, value: optionValue }) => (
          <SelectOption
            id={id}
            key={optionValue}
            label={optionLabel}
            selected={selectedValue}
            value={optionValue}
            onSelect={selectOptionHandler}
            dataTest={`${dataTest}-option-${optionValue}`}
            tooltip={tooltip}
            checkbox
          />
        )
      )}
    </SelectBase>
  )
})

export default FilterMultiSelect
